import { useMemo, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Printer } from 'lucide-react';
import api from '../utils/api';
import { formatUnitIdShort, ssCodeBare } from '../utils/unitIdFormat';
import { resolveUnitTypeDisplayName } from '../utils/unitDisplay';
import {
  INSPECTION_RESULT,
  normalizeInspectionResult,
  inspectionResultChipClass,
} from '../utils/inspectionResultColor';

function toPlanPercent(x, y) {
  if (x == null || y == null || x === '' || y === '') return null;
  const nx = Number(x);
  const ny = Number(y);
  if (!Number.isFinite(nx) || !Number.isFinite(ny)) return null;
  if (nx >= 0 && nx <= 1 && ny >= 0 && ny <= 1) return { x: nx * 100, y: ny * 100 };
  return { x: nx, y: ny };
}

function sanitizeSeg(s) {
  return String(s || '')
    .trim()
    .replace(/[/\\:*?"<>|]/g, '-')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '')
    .toLowerCase();
}

function filenameFromUrl(url) {
  const s = String(url || '').split('?')[0];
  const m = s.match(/([^/\\]+\.(png|jpe?g|webp|gif|pdf|svg))$/i);
  return m ? m[1] : '';
}

function unitLabel(unit) {
  const fullId = formatUnitIdShort(unit.unique_id, unit.ss_code);
  const bare = ssCodeBare(unit.ss_code);
  let label = fullId || bare || '?';
  if (bare && label) {
    const prefix = `${bare}-`;
    if (label.toLowerCase().startsWith(prefix.toLowerCase())) {
      label = label.slice(prefix.length) || label;
    }
  }
  return label;
}

function ReadOnlyMarker({ unit }) {
  const pos = toPlanPercent(unit.floor_plan_x, unit.floor_plan_y);
  if (!pos) return null;
  const result = normalizeInspectionResult(
    unit.inspection_result || unit.inspect_status || unit.last_outcome || ''
  );
  let colorClass = 'border-primary-600 text-primary-800 bg-white';
  if (
    result === INSPECTION_RESULT.PASS ||
    result === INSPECTION_RESULT.FAIL ||
    result === INSPECTION_RESULT.INCOMPLETE
  ) {
    colorClass = inspectionResultChipClass(result);
  }
  const typeName = String(resolveUnitTypeDisplayName(unit) || '').trim();
  const fullId = formatUnitIdShort(unit.unique_id, unit.ss_code);
  return (
    <div
      className={`absolute z-10 select-none px-1 py-0.5 font-bold leading-none border-2 shadow-md text-center whitespace-nowrap text-[10px] ${colorClass}`}
      style={{
        left: `${pos.x}%`,
        top: `${pos.y}%`,
        transform: 'translate(-50%, -50%)',
      }}
      title={`${fullId}${typeName ? ` · ${typeName}` : ''}`}
    >
      {unitLabel(unit)}
    </div>
  );
}

/**
 * Read-only floor plan + unit pins for Documents preview.
 * Matches the open file to levels/plans by filename or Floor-Plan/{related}/ folder name.
 */
export default function DocumentFloorPlanView({ buildingId, doc, imageUrl }) {
  const stageRef = useRef(null);

  const { data: levelsSetup } = useQuery({
    queryKey: ['building', buildingId, 'levels-setup'],
    queryFn: async () => (await api.get(`/buildings/${buildingId}/levels-setup`)).data?.data,
    enabled: !!buildingId,
  });

  const { data: unitsList = [] } = useQuery({
    queryKey: ['building', buildingId, 'ss-units', 'floor-preview'],
    queryFn: async () => {
      const response = await api.get(`/buildings/${buildingId}/ss-units`, {
        params: { includeInactive: '1' },
      });
      const raw = response.data?.data ?? response.data;
      return Array.isArray(raw) ? raw : Array.isArray(raw?.units) ? raw.units : [];
    },
    enabled: !!buildingId,
  });

  const match = useMemo(() => {
    const fileName = String(doc?.storedName || doc?.name || '').split('/').pop();
    const pathParts = String(doc?.path || '')
      .replace(/\\/g, '/')
      .split('/')
      .filter(Boolean);
    const related =
      pathParts[0] === 'Floor-Plan' && pathParts[1] && !/^\d{4}-\d{2}-\d{2}$/.test(pathParts[1])
        ? pathParts[1]
        : '';
    const relatedKey = sanitizeSeg(related);
    const plans = Array.isArray(levelsSetup?.plans) ? levelsSetup.plans : [];
    const levels = Array.isArray(levelsSetup?.levels) ? levelsSetup.levels : [];

    const matchedPlanIds = new Set();
    for (const p of plans) {
      const urlName = filenameFromUrl(p.plan_url);
      if (fileName && urlName && urlName === fileName) matchedPlanIds.add(String(p.id));
      if (relatedKey && (sanitizeSeg(p.label) === relatedKey || sanitizeSeg(p.code) === relatedKey)) {
        matchedPlanIds.add(String(p.id));
      }
    }

    const levelIds = new Set();
    for (const lv of levels) {
      const planId = String(lv.floor_plan_id || lv.plan_id || '');
      if (matchedPlanIds.size && planId && matchedPlanIds.has(planId)) {
        levelIds.add(String(lv.id));
        continue;
      }
      if (relatedKey) {
        const placeKey = sanitizeSeg(lv.place_name || lv.label || lv.code || '');
        if (placeKey && placeKey === relatedKey) levelIds.add(String(lv.id));
      }
    }

    // Building-plan / no match: use levels that share the same image filename if any
    if (!levelIds.size && fileName) {
      for (const lv of levels) {
        const urlName = filenameFromUrl(lv.plan_url);
        if (urlName && urlName === fileName) levelIds.add(String(lv.id));
      }
    }

    return {
      fileName,
      related,
      levelIds: [...levelIds],
      matchedPlanIds: [...matchedPlanIds],
    };
  }, [doc, levelsSetup]);

  const units = useMemo(() => {
    const placed = (unitsList || []).filter((u) => toPlanPercent(u.floor_plan_x, u.floor_plan_y));
    if (!match.levelIds.length) {
      // Fallback: all placed units on generic Building-plan copies
      return relatedIsGeneric(match.related) ? placed : [];
    }
    const idSet = new Set(match.levelIds.map(String));
    return placed.filter((u) => u.level_id != null && idSet.has(String(u.level_id)));
  }, [unitsList, match]);

  const printWithUnits = async () => {
    if (!imageUrl) return;
    // New window cannot use this page's blob: URL — convert to data URL first.
    let src = imageUrl;
    try {
      if (String(imageUrl).startsWith('blob:')) {
        const res = await fetch(imageUrl);
        const blob = await res.blob();
        src = await new Promise((resolve, reject) => {
          const fr = new FileReader();
          fr.onload = () => resolve(fr.result);
          fr.onerror = reject;
          fr.readAsDataURL(blob);
        });
      }
    } catch (_) {
      /* fall back to original URL */
    }
    const w = window.open('', '_blank');
    if (!w) return;
    const markersHtml = units
      .map((u) => {
        const pos = toPlanPercent(u.floor_plan_x, u.floor_plan_y);
        if (!pos) return '';
        const label = unitLabel(u).replace(/</g, '&lt;');
        return `<div style="position:absolute;left:${pos.x}%;top:${pos.y}%;transform:translate(-50%,-50%);border:2px solid #1d4ed8;background:#fff;padding:2px 4px;font:bold 11px sans-serif;white-space:nowrap">${label}</div>`;
      })
      .join('');
    const title = String(doc?.name || 'Floor plan').replace(/</g, '&lt;');
    w.document.write(`<!DOCTYPE html><html><head><title>${title}</title>
      <style>
        body{margin:16px;font-family:sans-serif;color:#111}
        h1{font-size:16px;margin:0 0 12px}
        .stage{position:relative;display:inline-block;max-width:100%}
        .stage img{display:block;max-width:100%;height:auto}
        @media print{body{margin:0}.no-print{display:none}}
      </style></head><body>
      <h1>${title}</h1>
      <p class="no-print" style="font-size:12px;color:#555">${units.length} unit pin(s) · close after print</p>
      <div class="stage">
        <img src="${src}" alt=""/>
        ${markersHtml}
      </div>
      <script>window.onload=function(){setTimeout(function(){window.print()},200)}</script>
      </body></html>`);
    w.document.close();
  };

  return (
    <div className="w-full flex flex-col items-center gap-2 min-h-0">
      <div className="flex flex-wrap items-center justify-center gap-2 text-xs text-gray-600">
        <span>
          {units.length} unit{units.length === 1 ? '' : 's'} on this plan
          {match.related ? ` · ${match.related.replace(/-/g, ' ')}` : ''}
          <span className="text-gray-400"> (view only)</span>
        </span>
        <button
          type="button"
          onClick={printWithUnits}
          className="inline-flex items-center gap-1 px-2 py-1 rounded border bg-white hover:bg-gray-50 text-gray-800"
        >
          <Printer className="w-3.5 h-3.5" /> Print with units
        </button>
      </div>
      <div
        ref={stageRef}
        className="relative inline-block max-w-full bg-slate-200 border border-slate-300 rounded overflow-hidden"
      >
        <img
          src={imageUrl}
          alt={doc?.name || 'Floor plan'}
          className="block max-w-full max-h-[48vh] h-auto w-auto select-none"
          draggable={false}
        />
        {/* Markers sit on the image box (inline-block wrapper matches rendered img size). */}
        <div className="absolute left-0 top-0 w-full h-full pointer-events-none">
          {units.map((u) => (
            <ReadOnlyMarker key={u.id} unit={u} />
          ))}
        </div>
      </div>
      {!units.length ? (
        <p className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded px-2 py-1">
          No placed units matched this drawing. Place units on Building → Floor plan first, or open
          the level folder that matches the place name.
        </p>
      ) : null}
    </div>
  );
}

function relatedIsGeneric(related) {
  const k = sanitizeSeg(related || '');
  return !k || k === 'building-plan' || k === 'main' || k === 'building';
}

export function isFloorPlanDocument(doc) {
  if (!doc) return false;
  if (String(doc.category || '') === 'Floor-Plan') return true;
  return String(doc.path || '').replace(/\\/g, '/').startsWith('Floor-Plan/');
}
